import React from 'react';

/**
 * Where a portfolio page's species figures come from.
 *
 * The portfolio components are shared by two sets of routes: the live ones, which read
 * the Neo4j projection, and the internal-only grid-backed ones. Rather than copying
 * the component tree and letting the copies drift, the same components serve both and
 * the route declares which source they read.
 */
export type PortfolioSpeciesSource = 'projection' | 'grid';

/**
 * Defaults to the projection, which is what every existing mount expects. A component
 * rendered outside a provider therefore behaves exactly as it does today — the
 * property that keeps this change invisible to the live page.
 */
const PortfolioSpeciesSourceContext = React.createContext<PortfolioSpeciesSource>('projection');

type Props = {
    source: PortfolioSpeciesSource;
    children: React.ReactNode;
};

export const PortfolioSpeciesSourceProvider = ({ source, children }: Props) => (
    <PortfolioSpeciesSourceContext.Provider value={source}>{children}</PortfolioSpeciesSourceContext.Provider>
);

export const usePortfolioSpeciesSource = (): PortfolioSpeciesSource => React.useContext(PortfolioSpeciesSourceContext);

/** True where the species figures come from the prediction grid. */
export const useIsGridBackedSpecies = (): boolean => usePortfolioSpeciesSource() === 'grid';
